import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Stack,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Paper,
  Tab,
  Tabs,
  Typography,
  useTheme,
  Drawer,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridExpandMoreIcon } from "@mui/x-data-grid";
import { ErrorOutline, MenuOpen, Terminal } from "@mui/icons-material";
import TemplateCard from "../templateCard";
import EditorMenu from "../../../componentes/editorMenu";
import useStyles from "./styles";
import { ptBR } from "@mui/x-data-grid/locales";
import MenuIcon from "@mui/icons-material/Menu";
import JsonEditor from "../../../componentes/jsonEditor";
import Loading from "../../../componentes/loading";
import { Panel, PanelGroup } from "react-resizable-panels";
import ResizeHandle from "../../../componentes/resizeHandle";
import { useMediaQuery } from "@mui/system";
import { useNavigate } from "react-router-dom";

interface CardEditorProps {
  isCardLoading?: any;
  firstLoading?: boolean;
  fielteredCard: DashCardsGraphics;
  updateCard: (newCard: DashCardsGraphics) => void;
  duplicateCard?: (card: DashCardsGraphics) => void;
  handleDelete: (id: string) => void;
  pageId: string;
  filtersInitialized: boolean;
}

const CardEditor: React.FC<CardEditorProps> = ({
  isCardLoading,
  firstLoading,
  fielteredCard,
  updateCard,
  duplicateCard,
  pageId,
  handleDelete,
  filtersInitialized,
}) => {
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState<number>(0);
  const [errorQuery, setErrorQuery] = useState<string>("");
  const [loadingQuery, setLoadingQuery] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [boxHeight, setBoxHeight] = useState<number | null>(null);
  const [selectedGraphicId, setSelectedGraphicId] = useState<string>("");
  const [resultQuery, setResultQuery] = useState<any[]>(() => {
    if (fielteredCard?.graphics?.[0]?.config) {
      return fielteredCard.graphics[0].config.data || [];
    }
    return [];
  });
  const [panelResizeKey, setPanelResizeKey] = useState(0);
  const theme = useTheme();
  const styles = useStyles(theme);
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const navigate = useNavigate();

  const fielteredCardMemo = useMemo(() => {
    return fielteredCard;
  }, [fielteredCard]);

  useEffect(() => {
    if (!selectedGraphicId || !fielteredCardMemo?.graphics) return;

    const newCard = {
      ...fielteredCardMemo,
      graphics: fielteredCardMemo.graphics.map((graphic) => {
        if (graphic.id === selectedGraphicId) {
          return {
            ...graphic,
            config: {
              ...graphic.config,
              data: resultQuery,
            },
          };
        }
        return graphic;
      }),
    };

    updateCard(newCard as DashCardsGraphics);
  }, [resultQuery]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "e") {
        const active = document.activeElement;
        if (
          active &&
          (active.tagName === "INPUT" ||
            active.tagName === "TEXTAREA" ||
            (active as HTMLElement).isContentEditable ||
            active.className.includes("native-edit-context"))
        ) {
          return;
        }
        const pathParts = window.location.pathname.split("/");
        if (pathParts.length > 2) {
          pathParts.pop();
          const newPath = pathParts.join("/");
          navigate(newPath);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const boxId = useMemo(
    () => `box-${Math.random().toString(36).substr(2, 9)}`,
    [fielteredCard],
  );

  const customRows = resultQuery.map((object, index) => ({
    ...object,
    id: index,
  }));

  const dynamicColumns = () => {
    // Busca primeiro resultado e extrai as chaves (para usar como field)
    const filteredFields = resultQuery && Object.keys(resultQuery[0]);
    const columns: any[] = [];
    // percorre as chaves e cria colunas dinamicas de acordo com as chaves
    filteredFields?.map((field, index) => {
      columns.push({
        id: index,
        field: field,
        headerName: field,
        flex: 1,
        minWidth: 150,
      });
    });
    return columns;
  };

  const handleDrawerToggle = () => {
    setOpenDrawer((prevState) => !prevState);
  };

  const handleChange = (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  useEffect(() => {
    if (errorQuery.length > 0) {
      setExpanded(true);
    }
  }, [errorQuery]);

  const json = () => {
    const graphics = fielteredCardMemo?.graphics?.map((graphic) => {
      const { data, metrics, ...config } = graphic.config;
      return { ...graphic, config };
    });
    return { ...fielteredCardMemo, graphics };
  };

  const handleResize = () => {
    const offsetHeight = window.innerHeight - 239;

    setBoxHeight(offsetHeight);
  };
  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        width: "100%",
        height: boxHeight ?? "100vh",
        gap: 4,
        paddingTop: 2,
      }}
    >
      <PanelGroup direction="horizontal">
        <Panel defaultSize={40}>
          <Box
            sx={{
              display: { xs: "flex", sm: "none" },
              justifyContent: "flex-end",
            }}
          >
            <Tooltip title="Editar">
              <IconButton onClick={() => handleDrawerToggle()}>
                <MenuIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <PanelGroup direction="vertical">
            <Panel
              defaultSize={40}
              onResize={() => setPanelResizeKey((k) => k + 1)}
            >
              <Box
                key={boxId + panelResizeKey}
                sx={{
                  height: "100%",
                  width: "100%",
                }}
              >
                <TemplateCard
                  index={1}
                  card={fielteredCardMemo}
                  firstLoading={firstLoading}
                  isEditing={true}
                  editMode={false}
                  handleDelete={handleDelete}
                  duplicateCard={duplicateCard}
                  paginaId={pageId}
                  filtersInitialized={filtersInitialized}
                />
              </Box>
            </Panel>
            <ResizeHandle />
            <Panel defaultSize={60}>
              <Paper
                sx={{
                  padding: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  height: "100%",
                  minHeight: "350px",
                  overflowY: "auto",
                }}
              >
                <Tabs
                  value={tabValue}
                  onChange={(e, newValue) => {
                    setTabValue(newValue);
                  }}
                  sx={{ height: "5%" }}
                >
                  <Tab
                    disableRipple
                    disableFocusRipple
                    label={"Resposta"}
                    iconPosition="start"
                    value={0}
                  />
                  <Tab
                    disableRipple
                    disableFocusRipple
                    label={"Json"}
                    iconPosition="start"
                    value={1}
                  />
                </Tabs>
                {tabValue === 0 ? (
                  <>
                    <Box
                      sx={{
                        borderRadius: "1rem",
                        border: `solid ${theme.palette.stroke}`,
                        borderWidth: "1px 1px 1px 1px",
                        height: "95%",
                        backgroundColor: "background.default",
                        overflowY: "auto",
                      }}
                    >
                      <Accordion
                        expanded={expanded}
                        onChange={handleChange}
                        sx={{
                          backgroundColor: "background.paper",
                          borderRadius: 0,
                          border: "none",
                          height: expanded ? "fit-content" : 50,
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<GridExpandMoreIcon />}
                          aria-controls="panel1-content"
                          id="panel1-header"
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Terminal />
                            <Typography component="span">Logs</Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              backgroundColor: "background.default",
                              padding: 2,
                              borderRadius: "1rem",
                              minHeight: 100,
                              maxHeight: "100%",
                            }}
                          >
                            {errorQuery && (
                              <>
                                <ErrorOutline
                                  sx={{
                                    fontSize: "16px",
                                    color: theme.palette.error.main,
                                    mt: 0.5,
                                    mr: 0.5,
                                  }}
                                />
                                <Typography sx={{ fontSize: "14px" }}>
                                  {errorQuery}
                                </Typography>
                              </>
                            )}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                      {loadingQuery && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <Loading size={120} />
                        </Box>
                      )}
                      {resultQuery && resultQuery.length > 0 && (
                        <DataGrid
                          columns={dynamicColumns()}
                          rows={customRows}
                          density="standard"
                          disableColumnMenu
                          disableColumnFilter
                          hideFooterSelectedRowCount
                          localeText={
                            ptBR.components.MuiDataGrid.defaultProps.localeText
                          }
                          sx={styles.dataGrid}
                          getRowClassName={(params) =>
                            params.indexRelativeToCurrentPage % 2 === 0
                              ? "even-row"
                              : "odd-row"
                          }
                          initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                          }}
                        />
                      )}
                    </Box>
                  </>
                ) : (
                  <Box
                    height={"95%"}
                    sx={{
                      backgroundColor: "background.default",
                      borderRadius: "1rem",
                    }}
                  >
                    <JsonEditor json={json()} disabled={true} />
                  </Box>
                )}
              </Paper>
            </Panel>
          </PanelGroup>
        </Panel>

        {!isXs && (
          <>
            <ResizeHandle isVertical={true} />
            <Panel defaultSize={60}>
              <Stack
                sx={{
                  display: { xs: "none", sm: "flex" },
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <EditorMenu
                  card={fielteredCardMemo}
                  updateCard={updateCard}
                  setResultQuery={setResultQuery}
                  setSelectedGraphicId={setSelectedGraphicId}
                  setErrorQuery={setErrorQuery}
                  setLoading={setLoadingQuery}
                  pageId={pageId}
                />
              </Stack>
            </Panel>
          </>
        )}
      </PanelGroup>

      <Drawer
        variant="temporary"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        sx={{
          display: { xs: "flex", sm: "none" },
          zIndex: 11000,
          "& .MuiDrawer-paper": {
            width: "100%",
          },
        }}
        anchor="right"
      >
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              padding: 2,
            }}
          >
            <Tooltip title="Voltar">
              <IconButton onClick={() => handleDrawerToggle()}>
                <MenuOpen />
              </IconButton>
            </Tooltip>
          </Box>
          <EditorMenu
            card={fielteredCardMemo}
            updateCard={updateCard}
            setResultQuery={setResultQuery}
            setSelectedGraphicId={setSelectedGraphicId}
            setErrorQuery={setErrorQuery}
            setLoading={setLoadingQuery}
            pageId={pageId}
          />
        </>
      </Drawer>
    </Box>
  );
};

export default CardEditor;
